import { IContextualMenuItem } from "@fluentui/react";
import React from "react";
import { useSelector } from "react-redux";
import { ISearchOpenFileInfo } from "../../../src/taskpane/interfaces/ISearchOpenFileInfo";
import { IViewsDropdown } from "../../../src/taskpane/interfaces/IViewsDropdown";
import { RootState } from "../../../src/taskpane/redux";
import { InfoPanelMode } from "../../Panels/CMFileInfoPanel";
import { gridSelectedItems } from "../ContentManagerBody";
import { GridProps } from "../useContentManagerBody";
import { useMenuItem_Automation } from "./useMenuItem_Automation";
import { useMenuItem_Checkin } from "./useMenuItem_Checkin";
import { useMenuItem_Checkout } from "./useMenuItem_Checkout";
import { useMenuItem_ClearSearch } from "./useMenuItem_ClearSearch";
import { useMenuItem_CopyTo } from "./useMenuItem_CopyTo";
import { useMenuItem_CurrentLocation } from "./useMenuItem_CurrentLocation";
import { useMenuItem_Delete } from "./useMenuItem_Delete";
import { useMenuItem_DiscardCheckout } from "./useMenuItem_DiscardCheckout";
import { useMenuItem_Download } from "./useMenuItem_Download";
import { useMenuItem_DownloadAsPDF } from "./useMenuItem_DownloadAsPDF";
import { useMenuItem_Error } from "./useMenuItem_Error";
import { useMenuItem_Filter } from "./useMenuItem_Filter";
import { useMenuItem_LoadingFiles } from "./useMenuItem_LoadingFiles";
import { useMenuItem_More } from "./useMenuItem_More";
import { useMenuItem_MoveTo } from "./useMenuItem_MoveTo";
import { useMenuItem_MoveToCancel } from "./useMenuItem_MoveToCancel";
import { useMenuItem_New } from "./useMenuItem_New";
import { useMenuItem_Open } from "./useMenuItem_Open";
import { useMenuItem_Pin } from "./useMenuItem_Pin";
import { useMenuItem_Preview } from "./useMenuItem_Preview";
import { useMenuItem_Properties } from "./useMenuItem_Properties";
import { useMenuItem_Publish } from "./useMenuItem_Publish";
import { useMenuItem_Refresh } from "./useMenuItem_Refresh";
import { useMenuItem_Rename } from "./useMenuItem_Rename";
import { useMenuItem_Replace } from "./useMenuItem_Replace";
import { useMenuItem_SavingFiles } from "./useMenuItem_SavingFiles";
import { useMenuItem_SelectedFileCount } from "./useMenuItem_SelectedFileCount";
import { useMenuItem_SelectedMoveFileCount } from "./useMenuItem_SelectedMoveFileCount";
import { useMenuItem_Share } from "./useMenuItem_Share";
import { useMenuItem_Unpublish } from "./useMenuItem_Unpublish";
import { useMenuItem_Upload } from "./useMenuItem_Upload";
import { useMenuItem_VersionAppRej } from "./useMenuItem_VersionAppRej";
import { useMenuItem_VersionHistory } from "./useMenuItem_VersionHistory";
import { useMenuItem_Views } from "./useMenuItem_Views";
import { useMenuItem_ClearFilter } from "./useMenuItem_ClearFilter";
import { useMenuItem_SaveAsPDF } from "./useMenuItem_SaveAsPDF";
import { useMenuItem_SigningRequest } from "./useMenuItem_SigningRequest";
import { useMenuItem_ShareToTeams } from "./useMenuItem_ShareToTeams";
import { ModeEnum } from "../../../src/taskpane/components/enums/modeEnum";
import { useMenuItem_NewDatasourceDocumentTemplate } from "./useMenuItem_NewDatasourceFileTemplate";
import { NewDocumentTemplate } from "../../../src/taskpane/interfaces/IDocumentTemplate";
import { SharePanelMode } from "../../Panels/CMFileSharePanel";
import { useMenuItem_CopyLink } from "./useMenuItem_CopyLink";
import { useMenuItem_HixUpload } from "./useMenuItem_HixUpload";
import { useMenuItem_ScanCustomFields } from "./useMenuItem_ScanCustomFields";
import { useMenuItem_SuggestPlaceholders } from "./useMenuItem_SuggestPlaceholders";
import { buildMenuItems, ICommandBarMenuProps } from "./buildMenuItems";
import { resolveCopyMoveTrayState } from "../helpers/copyMoveTrayState";
import { useMenuItem_ShareWithGroup } from "./useMenuItem_ShareWithGroup";
import { IClientGroupShareGroup } from "../../../src/taskpane/services/clientGroupShareGroups";

// Stable fallback so a host without the panel does not hand the memo a new function per render.
const NO_SHARE_WITH_GROUP_HOST = (): void => { /* no host panel */ };

export function useMenuItems(
    mode: ModeEnum,
    selectedItems: any[],
    waitFor: boolean,
    setWaitFor: (waitFor: boolean) => void,
    waitForContext: string,
    setWaitForContext: (waitForContext: string) => void,
    isInOneDrive: boolean,
    listFields: any,
    selectedItemInfo?: ISearchOpenFileInfo,
    setIsCMCreateFolderPanelOpen?: (isCMCreateFolderPanelOpen: boolean) => void,
    setIsCMUploadFilesPanelOpen?: (isCMUploadFilesPanelOpen: boolean) => void,
    setShowRenameDialog?: (showRenameDialog: boolean) => void,
    setIsCMFileInfoPanelOpen?: (isCMFileInfoPanelOpen: boolean) => void,
    setInfoPanelMode?: (infoPanelMode: InfoPanelMode) => void,
    setApprovalFiles?: (approvalFiles: any[]) => void,
    setIsApprovalsPanelOpen?: (isApprovalsPanelOpen: boolean) => void,
    setShowDeleteDialog?: (showDeleteDialog: boolean) => void,
    setShowVersionHistoryDialog?: (showVersionHistoryDialog) => void,
    setIsPublish?: (isPublish: boolean) => void,
    setShowCheckinDialog?: (showCheckinDialog: boolean) => void,
    gridProps?: GridProps,
    setGridProps?: (gridProps: GridProps) => void,
    setSelectedItems?: (selectedItems: gridSelectedItems) => void,
    setIsCMFilePreviewPanelOpen?: (isCMFilePreviewPanelOpen: boolean) => void,
    setSelectionCleared?: (selectionCleared: boolean) => void,
    setGridItemIsBusy?: (items: any[], isBusy: boolean) => void,
    updateGridItems?: (items: any[]) => void,
    viewsWithColumns?: IViewsDropdown[],
    selectedView?: IViewsDropdown,
    setSelectedView?: (view: IViewsDropdown) => void,
    setIsCMErrorModalOpen?: (isCMErrorModalOpen: boolean) => void,
    setErrorMessage?: (errorMessage: string) => void,
    setIsCMVersionAppRejModalOpen?: (isCMVersionAppRejModalOpen: boolean) => void,
    setIsCMFileFilterPanelOpen?: (isCMFileFilterPanelOpen: boolean) => void,
    newDocumentTemplates?: NewDocumentTemplate[],
    openCreateTemplateModal?: (selectedTemplate: NewDocumentTemplate) => void,
    // quickFilterValue?: string,
    menuItem_QuickFilter?: IContextualMenuItem,
    setIsCMMoveFilesListPanelOpen?: (isCMMoveFilesListPanelOpen: boolean) => void,
    setShowConfirmMoveToDialog?: (isShowConfirmMoveToDialog: boolean) => void,
    isFetchingData?: boolean,
    openSharePanel?: (mode: SharePanelMode) => void,
    setShowCancelAssignmentDialog?: (showCancelAssignmentDialog: boolean) => void,
    libraryMinVerEnabled?: boolean,
    openDuplicateFileVersionSelectorModal?: (isDuplicate: boolean, showVersion: boolean) => void,
    reloadGrid?: (gridData?: GridProps) => void,
    pickFiles?: () => void,
    pickFolder?: () => void,
    setIsSigningPanelOpen?: (isSigningPanelOpen: boolean) => void,
    setIsFileUploadProgressPanelOpen?: (isFileUploadProgressPanelOpen: boolean) => void,
    setIsHixPanelOpen?: (isHixPanelOpen: boolean) => void,
    setScanCustomFieldsErrors?: (errors: string[], skipped?: string[]) => void,
    openSuggestPlaceholdersPanel?: (target: import("./useMenuItem_SuggestPlaceholders").ISuggestPlaceholdersTarget) => void,
    clientGroupShareGroups?: IClientGroupShareGroup[],
    openShareWithGroupPanel?: () => void,
) {
    const selectedItemCount: number = selectedItems?.length ?? 0;

    const { menuItem_Automation } = useMenuItem_Automation(selectedItems, waitFor, setApprovalFiles, setIsApprovalsPanelOpen);
    const { menuItem_Checkin } = useMenuItem_Checkin(selectedItems, waitFor, setIsPublish, setShowCheckinDialog, isInOneDrive);
    const { menuItem_Checkout } = useMenuItem_Checkout(selectedItems, waitFor, setWaitFor, isInOneDrive, setGridItemIsBusy, updateGridItems, setIsCMErrorModalOpen, setErrorMessage);
    const { menuItem_ClearSearch } = useMenuItem_ClearSearch(waitFor);
    const { menuItem_ClearFilter } = useMenuItem_ClearFilter(waitFor);
    const { menuItem_CopyTo } = useMenuItem_CopyTo(gridProps?.items?.length, selectedItems, setShowConfirmMoveToDialog, isInOneDrive, isFetchingData, setIsCMUploadFilesPanelOpen);
    const { menuItem_CurrentLocation } = useMenuItem_CurrentLocation(selectedItemCount === 0, isInOneDrive);
    const { menuItem_Delete, deleteSuccess } = useMenuItem_Delete(selectedItems, waitFor, setWaitFor, setShowDeleteDialog, gridProps, setGridProps, setSelectionCleared);
    const { menuItem_DiscardCheckout } = useMenuItem_DiscardCheckout(selectedItems, waitFor, setWaitFor, isInOneDrive, setGridItemIsBusy, updateGridItems, setIsCMErrorModalOpen, setErrorMessage);
    const { menuItem_Download } = useMenuItem_Download(listFields, selectedItems, waitFor, setWaitFor, isInOneDrive, setWaitForContext);
    const { menuItem_DownloadAsPDF } = useMenuItem_DownloadAsPDF(listFields, selectedItems, waitFor, setWaitFor, isInOneDrive, setIsCMErrorModalOpen, setErrorMessage);
    const { menuItem_Error } = useMenuItem_Error(setIsCMUploadFilesPanelOpen, setIsCMMoveFilesListPanelOpen);
    const { menuItem_Filter } = useMenuItem_Filter(setIsCMFileFilterPanelOpen);
    const { menuItem_LoadingFiles } = useMenuItem_LoadingFiles(selectedItemCount, waitFor, waitForContext);
    const { menuItem_MoveTo } = useMenuItem_MoveTo(gridProps?.items?.length, selectedItems, setShowConfirmMoveToDialog, isInOneDrive, isFetchingData);
    const { menuItem_MoveToCancel } = useMenuItem_MoveToCancel();
    const { menuItem_New } = useMenuItem_New(selectedItemCount === 0, waitFor, newDocumentTemplates, openCreateTemplateModal);
    const { menuItem_Open } = useMenuItem_Open(selectedItems, waitFor, setWaitFor, isInOneDrive, selectedItemInfo);
    const { menuItem_Pin } = useMenuItem_Pin(selectedItems, waitFor, isInOneDrive, selectedItemInfo);
    const { menuItem_Preview, menuItem_Preview_IconOnly } = useMenuItem_Preview(selectedItems, setIsCMFilePreviewPanelOpen);
    const { menuItem_Properties, menuItem_Properties_IconOnly } = useMenuItem_Properties(selectedItems, setIsCMFileInfoPanelOpen, setInfoPanelMode);
    const { menuItem_Publish } = useMenuItem_Publish(selectedItems, waitFor, setWaitFor, setIsPublish, setShowCheckinDialog, isInOneDrive, setGridItemIsBusy, updateGridItems, setIsCMErrorModalOpen, setErrorMessage);
    const { menuItem_Refresh } = useMenuItem_Refresh();
    const { menuItem_Rename } = useMenuItem_Rename(selectedItems, waitFor, setShowRenameDialog);
    const { menuItem_Replace } = useMenuItem_Replace(selectedItems, waitFor, setIsCMFileInfoPanelOpen, setInfoPanelMode);
    const { menuItem_SaveAsPDF } = useMenuItem_SaveAsPDF(selectedItems, waitFor, setWaitFor, isInOneDrive, setIsCMErrorModalOpen, setErrorMessage, openDuplicateFileVersionSelectorModal, reloadGrid);
    const { menuItem_SavingFiles } = useMenuItem_SavingFiles(setIsCMMoveFilesListPanelOpen, setIsFileUploadProgressPanelOpen);
    const { menuItem_SelectedFileCount } = useMenuItem_SelectedFileCount(selectedItemCount, waitFor, setSelectionCleared);
    const { menuItem_SelectedMoveFileCount } = useMenuItem_SelectedMoveFileCount(setIsCMMoveFilesListPanelOpen, isInOneDrive);
    const { menuItem_Share } = useMenuItem_Share(selectedItemCount === 1, openSharePanel);
    const { menuItem_CopyLink } = useMenuItem_CopyLink(selectedItemCount > 0, openSharePanel);
    const { menuItem_SigningRequest } = useMenuItem_SigningRequest(selectedItems, waitFor, setApprovalFiles, setIsSigningPanelOpen);
    const { menuItem_Unpublish } = useMenuItem_Unpublish(selectedItems, waitFor, setWaitFor, isInOneDrive, setGridItemIsBusy, updateGridItems, setIsCMErrorModalOpen, setErrorMessage);
    const { menuItem_Upload } = useMenuItem_Upload(selectedItemCount === 0, waitFor, newDocumentTemplates, pickFiles, pickFolder, setIsCMUploadFilesPanelOpen);
    const { menuItem_VersionAppRej } = useMenuItem_VersionAppRej(selectedItems, waitFor, setIsCMVersionAppRejModalOpen, isInOneDrive);
    const { menuItem_VersionHistory } = useMenuItem_VersionHistory(selectedItems, waitFor, setShowVersionHistoryDialog, isInOneDrive);
    // const { menuItem_Views } = useMenuItem_Views(viewsWithColumns, selectedView, setSelectedView, quickFilterValue, libraryMinVerEnabled);
    const { menuItem_Views } = useMenuItem_Views(viewsWithColumns, selectedView, setSelectedView, libraryMinVerEnabled);
    const { menuItem_More } = useMenuItem_More(menuItem_Checkout, menuItem_Checkin, menuItem_DiscardCheckout, menuItem_Publish, menuItem_Unpublish, menuItem_VersionAppRej);
    const { menuItem_ShareToTeams } = useMenuItem_ShareToTeams(selectedItems, isInOneDrive, setWaitFor, setWaitForContext, mode);
    const { menuItem_ShareWithGroup } = useMenuItem_ShareWithGroup(selectedItems, clientGroupShareGroups, openShareWithGroupPanel ?? NO_SHARE_WITH_GROUP_HOST);
    const { menuItem_NewDatasourceDocumentTemplate } = useMenuItem_NewDatasourceDocumentTemplate(selectedItemCount === 0, waitFor, newDocumentTemplates, openCreateTemplateModal);
    const { menuItem_HixUpload } = useMenuItem_HixUpload(selectedItems, setIsHixPanelOpen);
    const { menuItem_ScanCustomFields, menuItem_ScanningTemplates } = useMenuItem_ScanCustomFields(selectedItems, waitFor, setScanCustomFieldsErrors);
    const { menuItem_SuggestPlaceholders } = useMenuItem_SuggestPlaceholders(selectedItems, waitFor, openSuggestPlaceholdersPanel ?? (() => { /* no host panel */ }));

    const moveToEnabled = useSelector((state: RootState) => state.contentManagerDirectory.moveTo.moveToEnabled);
    const moveToMode = useSelector((state: RootState) => state.contentManagerDirectory.moveTo.mode);
    // Resolved through the tray's own predicate rather than re-derived, so the two can
    // never disagree about which modes the tray covers. Visibility depends only on the
    // mode and the enabled flag — no destination or permission input is needed here.
    const copyMoveTrayVisible = React.useMemo(
        () => resolveCopyMoveTrayState({ moveTo: { moveToEnabled, mode: moveToMode } }).visible,
        [moveToEnabled, moveToMode]
    );

    const { commandBarMenu, contextMenu } = React.useMemo(() => {
        return buildMenuItems({
            mode,
            moveToEnabled,
            copyMoveTrayVisible,
            menuItems: {
                automation: menuItem_Automation,
                checkin: menuItem_Checkin,
                checkout: menuItem_Checkout,
                clearFilter: menuItem_ClearFilter,
                clearSearch: menuItem_ClearSearch,
                copyLink: menuItem_CopyLink,
                copyTo: menuItem_CopyTo,
                currentLocation: menuItem_CurrentLocation,
                delete: menuItem_Delete,
                discardCheckout: menuItem_DiscardCheckout,
                download: menuItem_Download,
                downloadAsPDF: menuItem_DownloadAsPDF,
                hixUpload: menuItem_HixUpload,
                error: menuItem_Error,
                filter: menuItem_Filter,
                loadingFiles: menuItem_LoadingFiles,
                more: menuItem_More,
                moveTo: menuItem_MoveTo,
                moveToCancel: menuItem_MoveToCancel,
                new: menuItem_New,
                newDatasourceDocumentTemplate: menuItem_NewDatasourceDocumentTemplate,
                open: menuItem_Open,
                pin: menuItem_Pin,
                preview: menuItem_Preview,
                previewIconOnly: menuItem_Preview_IconOnly,
                properties: menuItem_Properties,
                propertiesIconOnly: menuItem_Properties_IconOnly,
                publish: menuItem_Publish,
                quickFilter: menuItem_QuickFilter,
                refresh: menuItem_Refresh,
                rename: menuItem_Rename,
                replace: menuItem_Replace,
                saveAsPDF: menuItem_SaveAsPDF,
                scanCustomFields: menuItem_ScanCustomFields,
                scanningTemplates: menuItem_ScanningTemplates,
                suggestPlaceholders: menuItem_SuggestPlaceholders,
                savingFiles: menuItem_SavingFiles,
                selectedFileCount: menuItem_SelectedFileCount,
                selectedMoveFileCount: menuItem_SelectedMoveFileCount,
                share: menuItem_Share,
                shareWithGroup: menuItem_ShareWithGroup,
                shareToTeams: menuItem_ShareToTeams,
                signingRequest: menuItem_SigningRequest,
                unpublish: menuItem_Unpublish,
                upload: menuItem_Upload,
                versionAppRej: menuItem_VersionAppRej,
                versionHistory: menuItem_VersionHistory,
                views: menuItem_Views,
            }
        });
    }, [
        mode,
        moveToEnabled,
        copyMoveTrayVisible,
        menuItem_Automation,
        menuItem_Checkin,
        menuItem_Checkout,
        menuItem_ClearSearch,
        menuItem_ClearFilter,
        menuItem_CopyLink,
        menuItem_CopyTo,
        menuItem_CurrentLocation,
        menuItem_Delete,
        menuItem_DiscardCheckout,
        menuItem_Download,
        menuItem_DownloadAsPDF,
        menuItem_Error,
        menuItem_Filter,
        menuItem_LoadingFiles,
        menuItem_MoveTo,
        menuItem_MoveToCancel,
        menuItem_More,
        menuItem_New,
        menuItem_Open,
        menuItem_Pin,
        menuItem_Preview,
        menuItem_Preview_IconOnly,
        menuItem_Properties,
        menuItem_Properties_IconOnly,
        menuItem_Publish,
        menuItem_QuickFilter,
        menuItem_Refresh,
        menuItem_Rename,
        menuItem_Replace,
        menuItem_SaveAsPDF,
        menuItem_SavingFiles,
        menuItem_SelectedFileCount,
        menuItem_SelectedMoveFileCount,
        menuItem_Share,
        menuItem_SigningRequest,
        menuItem_Unpublish,
        menuItem_Upload,
        menuItem_VersionAppRej,
        menuItem_VersionHistory,
        menuItem_Views,
        menuItem_ShareToTeams,
        menuItem_ShareWithGroup,
        menuItem_NewDatasourceDocumentTemplate,
        menuItem_HixUpload,
        menuItem_ScanCustomFields,
        menuItem_ScanningTemplates,
        menuItem_SuggestPlaceholders
    ]);

    return {
        contextMenu,
        commandBarMenu,
        deleteSuccess
    }
}
